import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type Workspace, workspaceApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

interface Props {
  onNext: () => void;
}

export function StepWorkspace({ onNext }: Props) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const workspace: Workspace | undefined = data?.workspace;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');

  const rename = useMutation({
    mutationFn: (n: string) => workspaceApi.patch({ name: n }),
    onSuccess: (res) => {
      qc.setQueryData(qk.workspace, res);
      toast.success('Workspace renamed');
      setEditing(false);
    },
    onError: () => toast.error('Failed to rename workspace'),
  });

  function startEdit() {
    setName(workspace?.name ?? '');
    setEditing(true);
  }

  function handleRename(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim()) rename.mutate(name.trim());
  }

  return (
    <div className="rounded-xl border border-border p-8">
      <IconTile tone="neutral">
        <Building2 className="h-5 w-5" />
      </IconTile>
      <h2 className="mt-4 text-lg font-semibold">Your workspace</h2>
      <p className="mt-1.5 text-[14px] text-muted-foreground">
        Brain has automatically created your workspace. Give it a name that makes sense for your
        group.
      </p>

      <div className="mt-6">
        {editing ? (
          <form
            onSubmit={(e) => {
              void handleRename(e);
            }}
            className="flex gap-2"
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Workspace name"
              autoFocus
            />
            <Button type="submit" size="sm" disabled={rename.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <div className="flex items-center gap-3 rounded-lg border border-border-subtle px-4 py-3">
            <div className="min-w-0 flex-1">
              <Label className="text-faint">Workspace name</Label>
              <p className="mt-0.5 text-[15px] font-medium">{workspace?.name ?? '…'}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={startEdit} aria-label="Rename workspace">
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Button size="cta" onClick={onNext} className="mt-6 w-full">
        Continue
      </Button>
    </div>
  );
}
